import React, {useState, useRef, memo} from 'react';
import {useTranslation} from 'react-i18next';
import styled from 'styled-components';
import cssConstants from '../../constants/cssConstants';
import {useTscTheme} from '../../context/themeProvider';
import {ICollectionNavigationResponse} from '../../model/appModel';
import {IMediaDetails} from '../../model/mediaDetails';
import ReactDOM from 'react-dom';
import {isMobile} from 'react-device-detect';
import {renderToStaticMarkup} from 'react-dom/server';
import MediaProcessingGlyph from '../../static/svg/MediaProcessingGlyph.svg';
import {
   ControlButton,
   Glyphs,
   TscButtonType,
   TscStyles,
   TscThemeName,
   TscThemeNames
} from '@techsmith/tsc-cloud-style-guide';

const CollectionNavigationPane = styled.div<{theme: TscThemeNames}>`
   background-color: ${props => props.theme === TscThemeName.dusk ? cssConstants.collectionNavigationPaneBackgroundColorDusk : cssConstants.collectionNavigationPaneBackgroundColorDawn};
   width: 100%;
   overflow: hidden;
   max-width: 64rem;
   display: flex;
   justify-content: space-between;
   background-size: cover;
   background-origin: border-box;
   border-radius: ${TscStyles.border.radius.md};
   border: ${props => props.theme === TscThemeName.dusk ? cssConstants.marketingPaneBorderDawn : 'none'};
   -webkit-box-align: center;
   align-items: center;
   align-content: flex-end;
   padding: 0.5rem 0.5rem 0.5rem 0.75rem;
   margin: 0;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
`;

const CollectionNameContainer = styled.div`
   display: flex;
   flex-direction: column;
   flex-grow: 1;
   overflow: hidden;
   white-space: nowrap;
   text-overflow: ellipsis;
   justify-content: center;
   gap: .25rem;
   height: 100%;
   align-items: flex-start;
`;

const CollectionTitle = styled.div<{theme: TscThemeNames}>`
   font-weight: 600;
   font-size: .75rem;
   line-height: .75rem;
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.medium};
   margin: 0;
   font-family: sans-serif;
   text-transform: uppercase;
`;

const CollectionLink = styled.a<{theme: TscThemeNames}>`
   color: ${props => props.theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.mediumDark};
   text-decoration: none;
   display: inline-block;
   overflow: hidden;  
   white-space: nowrap;  
   text-overflow: ellipsis;  
   font-weight: 600;
   font-size: 1rem;
   font-family: Figtree, sans-serif;
   line-height: 1rem;
   max-width: 100%;
   cursor: pointer;

   &:hover {
      text-decoration: underline;
   }
`;

const ButtonContainer = styled.div`
   display: flex;
   flex: 0 0 auto;
   align-items: center;
   -webkit-box-align: center;
   position:relative;
`;

const MediaPositionText = styled.span`
   display: flex;
   font-family: Figtree, sans-serif;
   font-size: 1rem;
   line-height: 1.25rem;
   font-weight: 400;
   margin: 0 .5rem;
   flex: 1 1 auto;
   text-overflow: ellipsis;
   overflow: hidden;
`;

const NavigationPreviewContainer = styled.div<{showNavigationPreview: boolean; top: number; right: number}>`
   border-radius: ${TscStyles.border.radius.lg};
   position: absolute;
   z-index:1;
   text-align: center;
   display: ${props => props.showNavigationPreview ? 'block' : 'none'};
   box-shadow: 0px 3px 12px 0px rgba(0, 0, 0, 0.15), 0px 1px 3px 0px rgba(0, 0, 0, 0.10);
   ${props => props.right && `right: calc(100% - ${props.right + window.scrollX}px);`}; 

   &.hover-below {
      ${props => props.top && `top:  calc(${props.top + window.scrollY}px + .5rem);`};
   }

   &.hover-above {
      ${props => props.top && `top: calc(${props.top + window.scrollY}px - 6.5rem);`};
   }
`;

const CollectionNavigationPreviewContent = styled.div<{theme: TscThemeNames}>`
   height: 4rem;
   background: ${cssConstants.collectionNavigationPreviewBackground};
   border-radius: ${TscStyles.border.radius.lg};
   overflow: hidden;
   backdrop-filter: blur(8px);
   justify-content: flex-start;
   align-items: center;
   gap: .5rem;
   display: inline-flex;
   ${props => props.theme === TscThemeName.dusk && 'box-shadow: 0px 0.5px 1px 1px rgb(255 255 255 / 5%) inset'};
`;

const NavigationHoverDetails = styled.div`
   padding: .5rem 0 .5rem .75rem;
   flex-direction: column;
   justify-content: center;
   align-items: flex-start;
   gap: .25rem;
   display: inline-flex;
   max-width: 16rem;
   min-width: 9rem;
`;

const NavigationDirection = styled.div`
   color: ${cssConstants.collectionNavigationPaneNavigationDirectionColor};
   font-size: 14px;
   font-family: Figtree, sans-serif;
   font-weight: 600;
   text-transform: uppercase;
   word-wrap: break-word;
`;

const AdjacentMediaName = styled.div`
   color: ${TscStyles.color.global.white};
   max-width: 16rem;
   font-size: 14px;
   font-family: Figtree, sans-serif;
   font-weight: 600;
   word-wrap: break-word;
   word-break: break-all;
   display: -webkit-box;
   -webkit-line-clamp: 1;
   -webkit-box-orient: vertical;
   box-orient: vertical;
   overflow: hidden;
   text-overflow: ellipsis;
   text-align: left;
   width: max-content;
`;

const PreviewThumbnail = styled.div<{source: string}>`
   background-image: url(${props => props.source});
   transition: opacity 500ms ease 0s;
   opacity: 1;
   background-repeat: no-repeat;
   background-size: cover;
   background-position: center center;
   inset: 0px;
   width: 7.125rem;
   height: 4rem;
`;

export const MarketingPaneBase: React.FC<ICollectionNavigationPaneProps> = ({collectionNavigationResponse, sortMetric, collectionId}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [mediaDetails, setMediaDetails] = useState(null);
   const buttonHandlerRef = useRef(null);
   const [showNavigationPreview, setShowNavigationPreview] = useState(false);
   const [hoverIsNext, setHoverIsNext] = useState(false);
   const mediaProcessingSvgDataUri = `data:image/svg+xml;base64,${String(btoa(renderToStaticMarkup(<MediaProcessingGlyph />)))}`;
   const navigationHoverOffset = 64;

   const getPreviewDisplayClass = () => Number(buttonHandlerRef.current.getBoundingClientRect().bottom) + navigationHoverOffset > Math.max(document.documentElement.clientHeight, window.innerHeight) ? 'hover-above' : 'hover-below';

   const handleButtonHover = (hoveredMediaDetails: IMediaDetails, isNext: boolean) => {
      setMediaDetails(hoveredMediaDetails);
      setHoverIsNext(isNext);
      setShowNavigationPreview(true);
   };

   const handleButtonUnhover = () => {
      setShowNavigationPreview(false);
   };

   return (
      <CollectionNavigationPane
         data-test-id="collection-navigation-pane"
         theme={theme}
      >
         <CollectionNameContainer>
            <CollectionTitle theme={theme}>
               {t('collectionNavigation.title')}
            </CollectionTitle>
            <CollectionLink
               theme={theme}
               href={`/collections/${collectionId}`}
            >
               {collectionNavigationResponse.CollectionName}
            </CollectionLink>
         </CollectionNameContainer>
         <ButtonContainer ref={buttonHandlerRef}>
            <div
               onMouseEnter={collectionNavigationResponse.PreviousMediaDetails && !isMobile ? () => handleButtonHover(collectionNavigationResponse.PreviousMediaDetails, false) : null}
               onMouseLeave={handleButtonUnhover}
            >
               <ControlButton
                  className="controlButton"
                  themeName={theme}
                  buttonType={TscButtonType.tertiary}
                  testId="collection-navigation-previous-button"
                  title={t('collectionNavigation.previous')}
                  glyph={<Glyphs.ArrowLeft16x16 />}
                  disabled={!collectionNavigationResponse.PreviousMediaDetails}
                  href={collectionNavigationResponse.PreviousMediaDetails ? `/${collectionNavigationResponse.PreviousMediaDetails.Hash}?collection=${collectionId}&sort=${sortMetric}` : null}
               />
            </div>
            <MediaPositionText tabIndex={0}>
               {t('collectionNavigation.positionMessage', {currentMediaPosition: collectionNavigationResponse.CurrentMediaPosition, collectionMediaCount: collectionNavigationResponse.MediaCount})}
            </MediaPositionText>
            <div
               onMouseEnter={collectionNavigationResponse.NextMediaDetails && !isMobile ? () => handleButtonHover(collectionNavigationResponse.NextMediaDetails, true) : null}
               onMouseLeave={handleButtonUnhover}
            >
               <ControlButton
                  className="controlButton"
                  themeName={theme}
                  buttonType={TscButtonType.tertiary}
                  testId="collection-navigation-next-button"
                  title={t('collectionNavigation.next')}
                  glyph={<Glyphs.ArrowRight16x16 />}
                  disabled={!collectionNavigationResponse.NextMediaDetails}
                  href={collectionNavigationResponse.NextMediaDetails ? `/${collectionNavigationResponse.NextMediaDetails.Hash}?collection=${collectionId}&sort=${sortMetric}` : null}
               />
            </div>
         </ButtonContainer>
         {ReactDOM.createPortal(
            <>
               {mediaDetails && buttonHandlerRef && buttonHandlerRef.current && buttonHandlerRef.current.getBoundingClientRect() && (
                  <NavigationPreviewContainer
                     data-test-id="collection-navigation-preview-hover"
                     className={getPreviewDisplayClass()}
                     showNavigationPreview={showNavigationPreview}
                     top={buttonHandlerRef.current.getBoundingClientRect().bottom}
                     right={buttonHandlerRef.current.getBoundingClientRect().right}
                  >
                     <CollectionNavigationPreviewContent theme={theme}>
                        <NavigationHoverDetails>
                           <NavigationDirection>
                              {hoverIsNext ? t('collectionNavigation.next') : t('collectionNavigation.previous')}
                           </NavigationDirection>
                           <AdjacentMediaName>
                              {mediaDetails.Title}
                           </AdjacentMediaName>
                        </NavigationHoverDetails>
                        <PreviewThumbnail source={mediaDetails.ThumbnailUrl ? mediaDetails.ThumbnailUrl : mediaProcessingSvgDataUri} />
                     </CollectionNavigationPreviewContent>
                  </NavigationPreviewContainer>
               )}
            </>,
            document.body
         )}
      </CollectionNavigationPane>
   );
};

export interface ICollectionNavigationPaneProps {
   collectionNavigationResponse: ICollectionNavigationResponse;
   sortMetric: string;
   collectionId: string;
}

export default memo(MarketingPaneBase);
